<script lang="ts" setup generic="V = any">
import { computed, ref, watch } from 'vue';
import { IconCheckboxUnchecked, IconCheckboxChecked, IconCheckboxMixed, IconCheckboxSquare } from '@/icons';
import { hasSlotContent } from '../../utils';
import { areValuesEqual } from '../../../shared/utils';
import type { TrackBy } from '../../../shared/types';

type Props<Multiselect = boolean, ModelValue = Multiselect extends true ? V[] : V> = {
  ariaLabel?: string;
  disabled?: boolean;
  indeterminate?: boolean;
  // It's needed because Vue convert it to string if boolean type is not defined explicitly
  // <UiCheckbox multiselect />
  multiselect?: boolean & Multiselect;
  value?: V;
  falseValue?: V;
  trackBy?: TrackBy<V>;
  modelValue?: ModelValue;
  onBeforeChange?: (payload: {
    value: V;
    selected: boolean;
    nextSelected: boolean;
    event: MouseEvent | KeyboardEvent;
  }) => boolean | void;
};

const {
  ariaLabel,
  disabled = false,
  indeterminate = false,
  multiselect = false,
  value = true as V,
  falseValue = false as V,
  trackBy,
  onBeforeChange,
  ...props
} = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: Props['multiselect'] extends true ? V[] : V];
}>();

const modelValue = ref<V | V[]>(); // Have to leave it empty because cannot use [] or falseValue here

function updateModelValue(value: V | V[]) {
  modelValue.value = value;
  emit('update:modelValue', value as Props['multiselect'] extends true ? V[] : V);
}

watch(
  () => props.modelValue,
  (value) => {
    modelValue.value = value;
  },
  { immediate: true },
);

const checked = computed<boolean>(() => {
  if (typeof modelValue.value === 'undefined') {
    return false;
  }

  if (multiselect) {
    return Array.isArray(modelValue.value)
      ? modelValue.value.some((item) => areValuesEqual(item, value, trackBy))
      : false;
  }

  return !Array.isArray(modelValue.value) && areValuesEqual(modelValue.value, value, trackBy);
});

let defaultOnChangePrevented = false;

function onChange(event: Event): void {
  if (disabled) {
    return;
  }

  const input = event.target as HTMLInputElement;

  if (defaultOnChangePrevented) {
    // onBeforeChange returned false - prevent the change by syncing checkbox back to model state
    input.checked = checked.value;
    defaultOnChangePrevented = false;
    return;
  }

  if (multiselect) {
    if (!Array.isArray(modelValue.value)) {
      modelValue.value = [];
    }

    if (input.checked) {
      if (checked.value) {
        return;
      }
      updateModelValue([...modelValue.value, value]);
    } else {
      if (!checked.value) {
        return;
      }
      updateModelValue(modelValue.value.filter((item) => !areValuesEqual(item, value, trackBy)));
    }
  } else {
    updateModelValue(input.checked ? value : falseValue);
  }
}

function handleInputEvent(event: MouseEvent | KeyboardEvent) {
  if (!onBeforeChange) return;

  const result = onBeforeChange({
    value,
    selected: checked.value,
    nextSelected: !checked.value,
    event,
  });

  defaultOnChangePrevented = result === false;
}

function onClick(event: MouseEvent) {
  if (disabled) return;

  handleInputEvent(event);
}
</script>

<template>
  <label
    :class="[
      $style.root,
      {
        [$style.disabled]: disabled,
        [$style.checked]: checked,
        [$style.indeterminate]: indeterminate,
      },
    ]"
  >
    <input
      :aria-checked="indeterminate ? 'mixed' : checked"
      :aria-label="ariaLabel"
      :checked
      :disabled
      type="checkbox"
      :class="$style.input"
      @change="onChange"
      @click="onClick"
    />
    <span :class="$style.icon">
      <IconCheckboxMixed v-if="indeterminate" />
      <IconCheckboxChecked v-else-if="checked" />
      <IconCheckboxSquare v-else-if="disabled" />
      <IconCheckboxUnchecked v-else />
    </span>
    <span v-if="hasSlotContent($slots.default)" :class="$style.label">
      <slot />
    </span>
  </label>
</template>

<style lang="scss" module>
@use '../../styles/colors';
@use '../../styles/shared';
@use '../../styles/typography';

.root {
  position: relative;
  display: inline-flex;
  gap: 8px;
  vertical-align: middle;
  color: colors.$primary;
  font-family: typography.$font-family-primary;
  font-size: 13px;
  line-height: 16px;
  cursor: pointer;
  user-select: none;

  &.disabled {
    color: colors.$surface-40;
    cursor: not-allowed;
  }
}

.input {
  @include shared.sr-only();
}

.label {
  flex: 1 1 0%;
  min-width: 0;
}

.icon {
  width: 16px;
  height: 16px;
  flex: none;
  color: #d2d3d9;

  > svg {
    width: 100%;
    height: 100%;
  }
}

.root.indeterminate .icon,
.root.checked .icon {
  color: #010000;
}

.root.disabled .icon {
  color: #d2d3d9;
}
</style>
